import { cn } from '@/lib/utils';
import { LucideIcon, Radar, ShieldCheck, TerminalSquare } from 'lucide-react';
import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import CRLogo from '@/assets/CR.png';

export interface LoginVariantProps {
  error?: string;
  providers: string[];
  onPasswordSignIn?: (
    email: string,
    password: string,
    callbackUrl: string
  ) => Promise<any>;
  onOAuthSignIn?: (provider: string, callbackUrl: string) => Promise<any>;
}

export const LOGIN_EYEBROW = 'Human-in-the-loop red teaming';
export const LOGIN_HEADLINE_LEAD = 'Red-team at machine speed.';
export const LOGIN_HEADLINE_ACCENT = 'You stay in command.';
export const LOGIN_SUBCOPY =
  'Crimson Raven runs the recon, plans the attack, and drives your Kali sessions — then holds at every command until you approve it.';
export const LOGIN_CAPABILITIES = [
  '29 tools',
  'MCP',
  'STIX 2.1',
  'Per-user Kali'
];

const LOGIN_FEATURES: { icon: LucideIcon; title: string; body: string }[] = [
  {
    icon: ShieldCheck,
    title: 'You approve every command',
    body: 'Nothing touches a target until you sign off.'
  },
  {
    icon: TerminalSquare,
    title: 'Isolated Kali sessions',
    body: 'Per-user containers keep every operation contained.'
  },
  {
    icon: Radar,
    title: 'Live threat graphs',
    body: 'STIX, COA & operation timelines you can replay.'
  }
];

const monoClass = 'font-mono uppercase tracking-[0.28em]';

export function Mono({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(monoClass, className)}
      style={{ fontFamily: 'var(--font-mono)' }}
    >
      {children}
    </span>
  );
}

export function Eyebrow({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 text-[11px] text-primary',
        monoClass,
        className
      )}
      style={{ fontFamily: 'var(--font-mono)' }}
    >
      <span className="relative flex h-1.5 w-1.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
      </span>
      {LOGIN_EYEBROW}
    </span>
  );
}

export function RavenWordmark({
  className,
  markClassName = 'h-9 w-9',
  tagline = 'Red-Team Copilot'
}: {
  className?: string;
  markClassName?: string;
  tagline?: string | null;
}) {
  return (
    <div className={cn('flex items-center gap-3', className)}>
      <span
        className={cn(
          'shrink-0 overflow-hidden rounded-full border border-primary/40 bg-black',
          markClassName
        )}
      >
        <img
          src={CRLogo}
          alt="Crimson Raven"
          className="h-full w-full object-cover"
          draggable={false}
        />
      </span>
      <div className="flex flex-col leading-tight">
        <Mono className="text-[13px] font-semibold text-foreground">
          Crimson Raven
        </Mono>
        {tagline ? (
          <span className="text-xs text-muted-foreground">{tagline}</span>
        ) : null}
      </div>
    </div>
  );
}

export function Headline({ className }: { className?: string }) {
  return (
    <h1
      className={cn(
        'font-bold leading-[0.98] tracking-tight text-foreground',
        className
      )}
    >
      {LOGIN_HEADLINE_LEAD}{' '}
      <span className="text-primary">{LOGIN_HEADLINE_ACCENT}</span>
    </h1>
  );
}

export function FeatureList({ className }: { className?: string }) {
  return (
    <ul className={cn('space-y-4', className)}>
      {LOGIN_FEATURES.map(({ icon: Icon, title, body }) => (
        <li key={title} className="flex items-start gap-3">
          <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-primary/25 bg-primary/10 text-primary">
            <Icon className="h-3.5 w-3.5" />
          </span>
          <span className="space-y-0.5">
            <span className="block text-sm font-medium text-foreground">
              {title}
            </span>
            <span className="block text-xs text-muted-foreground">{body}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export function CapabilityRow({ className }: { className?: string }) {
  return (
    <div
      className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', className)}
    >
      {LOGIN_CAPABILITIES.map((item, i) => (
        <span key={item} className="flex items-center gap-x-3">
          {i > 0 ? (
            <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
          ) : null}
          <Mono className="text-[10px] text-muted-foreground">{item}</Mono>
        </span>
      ))}
    </div>
  );
}

export function AuthMeta({ className }: { className?: string }) {
  return (
    <div className={cn('space-y-3 text-center', className)}>
      <p className="text-sm text-muted-foreground">
        No access yet?{' '}
        <Link
          to="/signup"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          Register
        </Link>
      </p>
      <p className="text-sm text-muted-foreground">
        Need help?{' '}
        <Link
          to="/support"
          className="font-medium text-primary underline-offset-4 hover:underline"
        >
          Contact support
        </Link>
      </p>
      <Mono className="block text-[10px] text-muted-foreground/70">
        By signing in you accept this workspace&apos;s operational policy.
      </Mono>
    </div>
  );
}
